<template>
    <div
        class="z-50 fixed inset-y-0 flex flex-col bg-gray-750 shadow-r-lg overflow-x-hidden | transition-all duration-300"
        :class="navWidth"
        :style="{ paddingTop: `${bodySpacingTop}px` }"
        data-testid="expandable-main-nav-container"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
        v-click-away="onClickaway"
    >
        <div class="relative flex items-center justify-center shadow-lg h-18">
            <div
                class="changelog-selector | absolute top-0 right-0 mt-2.5"
                :class="expanded ? 'mr-1' : 'mr-2'"
            />

            <router-link class="w-full p-6" to="/">
                <div class="flex items-center w-52 px-3px">
                    <!-- Custom padding-x to center icon -->
                    <activix-crm-logo :icon-only="!expanded" />
                </div>
            </router-link>
        </div>

        <slot>
            <expandable-main-nav ref="nav" />
        </slot>
    </div>
</template>

<script>
    import { useUserStore } from '@/store/modules/user/store.js';
    import { mapState, mapActions } from 'pinia';
    import { useLayoutStore } from '@/store/modules/layout/store.js';
    import { useEventBus } from '@/hooks/useEventBus.ts';

    import ExpandableMainNav from '@/components/nav/ExpandableMainNav.vue';
    import ActivixCrmLogo from '@/components/ActivixCrmLogo.vue';
    import { isTouchDevice } from '@/utils/device.js';
    import { debounce } from 'lodash-es';

    const { on, off } = useEventBus();

    export default {
        name: 'ExpandableMainNavContainer',
        components: {
            ActivixCrmLogo,
            ExpandableMainNav,
        },

        data: () => ({
            isTouchDevice: isTouchDevice(),
        }),

        computed: {
            ...mapState(useLayoutStore, {
                bodySpacingTop: 'bodySpacingTop',
                expanded: 'navExpanded',
                locked: 'navLocked',
            }),

            navWidth() {
                if (this.expanded) {
                    return 'w-64';
                }

                if (this.isTouchDevice) {
                    return 'w-0';
                }

                return 'w-20';
            },
        },

        watch: {
            '$route.name'() {
                this.onClickaway();
            },

            '$i18n.locale'(locale) {
                this.$beamer.changeLocale(this.$i18n.getContentLanguage(locale).toUpperCase());
            },
        },

        methods: {
            ...mapActions(useLayoutStore, ['setNavExpanded', 'setNavLocked']),

            onClickaway(event) {
                if (!isTouchDevice() || !this.expanded) {
                    return;
                }

                const sidebarToggle = document.querySelector('.sidebar-toggle');

                if (
                    !event ||
                    (!event.target.classList.contains('sidebar-toggle') &&
                        !sidebarToggle.contains(event.target))
                ) {
                    this.setNavExpanded(false);
                }
            },

            toggle() {
                this.setNavExpanded(!this.expanded);
            },

            toggleNavLock() {
                const nowLocked = !this.locked;

                this.setNavLocked(nowLocked);
                this.setNavExpanded(!nowLocked);
            },

            onMouseEnter() {
                if (!this.locked && !this.isTouchDevice) {
                    this.debouncedSetNavExpanded(true);
                }
            },

            onMouseLeave() {
                if (!this.locked && !this.isTouchDevice) {
                    this.debouncedSetNavExpanded(false);
                }
            },

            // eslint-disable-next-line func-names
            debouncedSetNavExpanded: debounce(function (value) {
                this.setNavExpanded(value);
            }, 100),
        },

        created() {
            on('toggle-nav', this.toggle);
            on('toggle-nav-lock', this.toggleNavLock);
        },

        async mounted() {
            await this.$nextTick();

            this.$beamer.init(useUserStore().parentAuthUser);
        },

        beforeUnmount() {
            off('toggle-nav', this.toggle);
            off('toggle-nav-lock', this.toggleNavLock);
        },
    };
</script>
